import React from 'react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import i18n from '../../i18n';
import { ReactComponent as BxsStoreIcon } from '../../static/images/svg/bxs_store.svg';
import { MenuStoreCommonDialog } from './menu-store-common-dialog.component';

type UseNowStoreOption = {
    /** 门店 string_id。 */
    value: string;
    /** 门店展示名称。 */
    label: string;
};

type MenuStoreUseNowDialogProps = {
    /** 弹窗开关。 */
    open: boolean;
    /** 菜单名称。 */
    menuName: string;
    /** 发布日期文本。 */
    dateLabel: string;
    /** 发布人文本。 */
    publisher: string;
    /** 激活日期文本；缺省时弹窗展示「待后端」占位（Figma 3594:30383）。 */
    activationDateLabel?: string;
    /** 兑换码文本；缺省时同上。 */
    redemptionCodeLabel?: string;
    /** 菜单图片样式变量。 */
    menuImageStyle: React.CSSProperties;
    /** 当前已选门店 `string_id` 列表（点击行切换；可多选）。 */
    useNowStoreIds: string[];
    /** 门店选项列表。 */
    useNowStoreOptions: UseNowStoreOption[];
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 切换某门店选中状态（再次点击同一行则取消选中）。 */
    onToggleUseNowStore: (value: string) => void;
    /** 提交使用。 */
    onSubmit: () => void;
    /** 提交中状态。 */
    submitting: boolean;
};

/**
 * 立刻使用弹窗（Figma `3594:30383`：使用菜单、菜单信息卡 2×2 元数据、目标门店列表、取消/立即分发）。
 */
export function MenuStoreUseNowDialog(props: MenuStoreUseNowDialogProps): React.ReactElement | null {
    /** 组件属性。 */
    const {
        open,
        menuName,
        dateLabel,
        publisher,
        activationDateLabel,
        redemptionCodeLabel,
        menuImageStyle,
        useNowStoreIds,
        useNowStoreOptions,
        onClose,
        onToggleUseNowStore,
        onSubmit,
        submitting,
    } = props;
    /** 已选门店数量，用于禁用「立即分发」与副文案。 */
    const selectedStoreCount = useNowStoreIds.length;
    /** 无后端字段时的统一占位（与稿中「待返回」语义一致，使用一字线避免与真实数据混淆）。 */
    const pendingDisplay = i18n.t('menu_store_use_now_field_pending');
    /** 激活日期格展示文案。 */
    const activationCell = (activationDateLabel ?? '').trim() || pendingDisplay;
    /** 兑换码格展示文案。 */
    const redemptionCell = (redemptionCodeLabel ?? '').trim() || pendingDisplay;
    if (!open) {
        return null;
    }
    return (
        <MenuStoreCommonDialog
            title={i18n.t('menu_store_use_now_title')}
            onClose={onClose}
            hideTitleIcon
            dialogClassName="menu-store-activate-dialog--use-now"
            hideBottomClose
            footer={(
                <>
                    {useNowStoreOptions.length === 0 ? (
                        <Typography variant="caption" color="error" className="menu-store-activate-dialog__error">
                            {i18n.t('menu_store_msg_no_restaurant')}
                        </Typography>
                    ) : null}
                    <Box className="menu-store-dialog__footer-row menu-store-dialog__footer-row--use-now">
                        <Button
                            type="button"
                            variant="contained"
                            disableElevation
                            className="menu-store-dialog__btn-cancel menu-store-dialog__btn-cancel--use-now"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {i18n.t('menu_store_dialog_cancel')}
                        </Button>
                        <LoadingOverlayButton
                            type="button"
                            variant="contained"
                            disableElevation
                            className="menu-store-dialog__btn-confirm menu-store-dialog__btn-confirm--use-now"
                            loading={submitting}
                            loadingIndicatorSize={22}
                            onClick={onSubmit}
                            disabled={useNowStoreOptions.length === 0 || selectedStoreCount === 0}
                        >
                            {i18n.t('menu_store_use_now_submit')}
                        </LoadingOverlayButton>
                    </Box>
                </>
            )}
        >
            <Box className="menu-store-activate-dialog__fields menu-store-dialog__use-now-fields">
                <Box className="menu-store-dialog__use-now-block">
                    <Typography variant="subtitle1" component="p" className="menu-store-dialog__section-label menu-store-dialog__section-label--use-now">
                        {i18n.t('menu_store_dialog_menu_info')}
                    </Typography>
                    <Box className="menu-store-dialog__meta-card menu-store-dialog__meta-card--use-now">
                        <Box className="menu-store-dialog__meta-thumb menu-store-dialog__meta-thumb--use-now" style={menuImageStyle} />
                        <Box className="menu-store-dialog__meta-grid menu-store-dialog__meta-grid--use-now">
                            <Box className="menu-store-dialog__use-now-name-block">
                                <Typography variant="body1" component="p" className="menu-store-dialog__use-now-name-label">
                                    {i18n.t('menu_store_dialog_menu_name')}
                                </Typography>
                                <Typography variant="body1" component="p" className="menu-store-dialog__use-now-name-value">
                                    {menuName}
                                </Typography>
                            </Box>
                            <Box className="menu-store-dialog__use-now-kv-grid">
                                <Box className="menu-store-dialog__use-now-kv-cell">
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-label">
                                        {i18n.t('menu_store_dialog_publish_time')}
                                    </Typography>
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-value">
                                        {dateLabel}
                                    </Typography>
                                </Box>
                                <Box className="menu-store-dialog__use-now-kv-cell">
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-label">
                                        {i18n.t('menu_store_dialog_publisher')}
                                    </Typography>
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-value">
                                        {publisher}
                                    </Typography>
                                </Box>
                                <Box className="menu-store-dialog__use-now-kv-cell">
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-label">
                                        {i18n.t('menu_store_dialog_activation_date')}
                                    </Typography>
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-value">
                                        {activationCell}
                                    </Typography>
                                </Box>
                                <Box className="menu-store-dialog__use-now-kv-cell">
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-label">
                                        {i18n.t('menu_store_dialog_redemption_code')}
                                    </Typography>
                                    <Typography variant="body2" component="p" className="menu-store-dialog__use-now-kv-value">
                                        {redemptionCell}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box className="menu-store-dialog__use-now-block">
                    <Box className="menu-store-dialog__use-now-store-header">
                        <Typography variant="subtitle1" component="p" className="menu-store-dialog__section-label menu-store-dialog__section-label--use-now">
                            {i18n.t('menu_store_dialog_target_store')} ({useNowStoreOptions.length})
                        </Typography>
                        <Typography variant="body2" component="p" className="menu-store-dialog__use-now-store-caption">
                            {i18n.t('menu_store_use_now_store_caption', {
                                selected: selectedStoreCount,
                                total: useNowStoreOptions.length,
                            })}
                        </Typography>
                    </Box>
                    <Box className="menu-store-dialog__store-list menu-store-dialog__store-list--use-now">
                        {useNowStoreOptions.map((option) => (
                            <ButtonBase
                                key={option.value}
                                disableRipple
                                className={['menu-store-dialog__store-item', 'menu-store-dialog__store-item--use-now', useNowStoreIds.includes(option.value) ? 'menu-store-dialog__store-item--active' : ''].filter(Boolean).join(' ')}
                                onClick={() => onToggleUseNowStore(option.value)}
                            >
                                <BxsStoreIcon className="menu-store-dialog__store-icon menu-store-dialog__store-icon--use-now" width={24} height={24} aria-hidden />
                                <Typography component="span" variant="body2" className="menu-store-dialog__store-text">
                                    {option.label}
                                </Typography>
                            </ButtonBase>
                        ))}
                    </Box>
                </Box>
            </Box>
        </MenuStoreCommonDialog>
    );
}
